import {
	Button,
	Card,
	ControlledCheckbox,
	ControlledInput,
	ControlledPhoneInput,
	HoverDetails,
	Input,
} from "@dune/ui";
import { Copy, KeyRound } from "lucide-react";
import React, { useCallback, useState } from "react";
import { useFormContext } from "react-hook-form";
import { toast } from "sonner";
import {
	useCreateBrokerApiKey,
	type CreateApiKeyResponse,
} from "../../../../../../api/mutations/api-keys.mutations";
import {
	useGetBrokerApiKeys,
	type ApiKeyResponse,
} from "../../../../../../api/queries/api-keys.queries";
import { useMe } from "../../../../../../api/queries/users.queries";

const formatExpirationDate = (dateStr: string) => {
	const date = new Date(dateStr);
	return date.toLocaleDateString("fr-FR", {
		day: "numeric",
		month: "long",
		year: "numeric",
	});
};

type Props = {
	brokerSocietyId: number;
	canGenerateApiKeys: boolean;
};

export const MainDetails: React.FC<Props> = ({
	brokerSocietyId,
	canGenerateApiKeys,
}) => {
	const {
		control,
		formState: { errors },
	} = useFormContext();

	const { me } = useMe();
	const isDuneUser = me?.role === "DUNE";

	const { data: apiKeys = [], isLoading: apiKeysLoading } = useGetBrokerApiKeys(
		brokerSocietyId,
		{ enabled: canGenerateApiKeys },
	);
	const createApiKeyMutation = useCreateBrokerApiKey(brokerSocietyId);

	const [newlyCreatedKey, setNewlyCreatedKey] =
		useState<CreateApiKeyResponse | null>(null);

	const latestKey: ApiKeyResponse | CreateApiKeyResponse | null =
		newlyCreatedKey ?? apiKeys[apiKeys.length - 1] ?? null;

	const displayedKey = newlyCreatedKey
		? newlyCreatedKey.key
		: latestKey
			? `${(latestKey as ApiKeyResponse).keyPrefix}••••••••`
			: null;

	const handleGenerate = useCallback(() => {
		createApiKeyMutation.mutate(undefined, {
			onSuccess: (data: CreateApiKeyResponse) => {
				setNewlyCreatedKey(data);
				toast.success("Clé API générée");
			},
		});
	}, [createApiKeyMutation]);

	const handleCopy = useCallback(async () => {
		if (!newlyCreatedKey?.key) return;
		try {
			await navigator.clipboard.writeText(newlyCreatedKey.key);
			toast.success("Clé API copiée dans le presse-papier");
		} catch {
			toast.error("Impossible de copier la clé");
		}
	}, [newlyCreatedKey]);

	return (
		<div className="py-4">
			<div className="flex flex-row w-full">
				<div className="w-full flex items-center">
					<div className="flex items-center gap-2 text-text-main-blue font-semibold">
						Informations société
						<HoverDetails
							content={
								<div>
									Pour toute modification des informations société, <br />
									envoyer un email à l'adresse{" "}
									<span className="underline">contact@duneassurance.com</span>
								</div>
							}
						/>
					</div>
				</div>
				<div className="w-full flex items-center">
					<div className="text-text-main-blue font-semibold">Gérant</div>
				</div>
			</div>
			<div className="flex flex-row gap-4 pb-3">
				<div className="w-full flex flex-col gap-4">
					<Card className="p-6 flex flex-col gap-4 overflow-auto text-text-secondary-blue">
						<div className="flex flex-row gap-4">
							<div className="flex flex-col items-start gap-3 w-full">
								<ControlledInput
									label="SIREN"
									control={control}
									name="siren"
									disabled
								/>
							</div>
						</div>
						<div className="flex flex-col items-start gap-3">
							<ControlledInput
								label="Dénomination"
								control={control}
								name="denomination"
								disabled
							/>
						</div>
						<div className="flex flex-col items-start gap-3">
							<div className="text-text-secondary-blue">
								Adresse du siège social
							</div>
							<ControlledInput
								control={control}
								name="headquartersAddress"
								disabled
							/>
						</div>
						{isDuneUser && (
							<div className="flex flex-col gap-3 pt-2 border-t border-gray-200">
								<ControlledCheckbox
									control={control}
									name="canGenerateApiKeys"
									id="can-generate-api-keys"
									label="Société autorisée à générer des clés API"
								/>
							</div>
						)}
						{canGenerateApiKeys && (
							<div className="flex flex-col gap-3 pt-2 border-t border-gray-200">
								<div className="flex items-center gap-2 text-text-secondary-blue">
									<KeyRound className="size-4" />
									Clé API
								</div>
								{apiKeysLoading ? (
									<div className="text-sm text-gray-500">Chargement...</div>
								) : displayedKey ? (
									<div className="flex flex-col gap-2">
										<div className="flex gap-2 items-center">
											<Input
												type={newlyCreatedKey ? "text" : "password"}
												value={displayedKey}
												readOnly
												className="font-mono text-sm"
											/>
											{newlyCreatedKey && (
												<Button
													type="button"
													variant="outline"
													size="icon"
													onClick={handleCopy}
													title="Copier la clé"
												>
													<Copy className="size-4" />
												</Button>
											)}
										</div>
										{latestKey && "expiresAt" in latestKey && (
											<div className="text-xs text-gray-500">
												Expire le :{" "}
												{formatExpirationDate(
													(latestKey as ApiKeyResponse).expiresAt,
												)}
											</div>
										)}
									</div>
								) : null}
								<Button
									type="button"
									variant="outline"
									size="sm"
									onClick={handleGenerate}
									disabled={createApiKeyMutation.isPending}
								>
									{createApiKeyMutation.isPending
										? "Génération..."
										: displayedKey
											? "Générer une nouvelle clé"
											: "Générer une clé API"}
								</Button>
							</div>
						)}
					</Card>
				</div>
				<div className="w-full flex flex-col gap-4">
					<Card className="p-6 flex flex-col gap-4 overflow-auto text-text-secondary-blue">
						<div className="flex flex-row gap-4">
							<div className="flex flex-col items-start gap-3 w-full">
								<ControlledInput
									label="Nom"
									control={control}
									name="lastname"
									disabled
									error={errors.lastname?.message?.toString()}
								/>
							</div>
							<div className="flex flex-col items-start gap-3 w-full">
								<ControlledInput
									label="Prénom"
									control={control}
									name="firstname"
									disabled
									error={errors.firstname?.message?.toString()}
								/>
							</div>
						</div>
						<div className="flex flex-col items-start gap-3">
							<ControlledInput
								label="Email"
								control={control}
								name="email"
								disabled
								error={errors.email?.message?.toString()}
							/>
						</div>
						<div className="flex flex-col items-start gap-3">
							<div className="text-text-secondary-blue">Mobile</div>
							<ControlledPhoneInput
								control={control}
								name="phone"
								placeholder="Ex : +33 1 23 45 67 89"
								defaultCountry="FR"
								disabled
								error={errors.phone?.message?.toString()}
							/>
						</div>
					</Card>
				</div>
			</div>
		</div>
	);
};
